/**
 * The one failure a contact EDIT has that a contact CREATE does not: the document
 * being edited is not in the signed-in user's own book.
 *
 * Every client-side contact write lands at
 * `dashboard-users-v2/{signed-in user}/contacts/{contactId}` — the path is keyed on
 * the VIEWER, never on the record's owner. That was harmless while the only list a
 * realtor could open was their own. It stopped being harmless the moment a screen
 * could show a row belonging to a colleague: a `set(..., { merge: true })` against a
 * document that isn't there does not fail, it MINTS one — a phantom contact in the
 * viewer's book carrying only the field they happened to edit.
 *
 * `AppContext.updateExistingContact` therefore writes with `update()`, which fails
 * natively (`not-found`) when the document is absent, and re-throws it as this error
 * so callers can tell "not yours / deleted" apart from "the network is down" and say
 * something true to the user.
 *
 * The same distinction covers a second, older case: the contact WAS the viewer's and
 * has since been deleted (another tab, the mobile app, a bulk delete). Resurrecting it
 * as a stub is exactly as wrong there.
 */
export class ContactNotInYourBookError extends Error {
  readonly contactId: string;

  constructor(contactId: string) {
    super(`Contact ${contactId} is not in the signed-in user's contacts`);
    this.name = "ContactNotInYourBookError";
    this.contactId = contactId;
    // tsconfig targets es5, where subclassing a built-in breaks the prototype chain
    // and `instanceof` silently returns false. Restoring it is what makes the guard
    // below able to prefer `instanceof` at all.
    Object.setPrototypeOf(this, ContactNotInYourBookError.prototype);
  }
}

/**
 * `instanceof` with a name fallback: an error can cross a module boundary that
 * bundled its own copy of this class, and a write path that silently stopped
 * recognising its own failure would report "save failed" for a case the UI has a
 * precise message for.
 */
export function isContactNotInYourBookError(error: unknown): error is ContactNotInYourBookError {
  if (error instanceof ContactNotInYourBookError) return true;
  return (error as { name?: string } | null)?.name === "ContactNotInYourBookError";
}

/** Hebrew copy for the case above — one wording, every surface that can hit it. */
export const CONTACT_NOT_IN_YOUR_BOOK_MESSAGE =
  "לא ניתן לערוך — הלקוח הזה אינו ברשימת הלקוחות שלך";

/**
 * A failed `sendWebotRequest`, carrying the two facts the message string cannot.
 *
 * ── Why a class ─────────────────────────────────────────────────────────────────
 *
 * `WebotServerContext` used to throw a bare `Error` with the status INTERPOLATED into
 * the message. That is readable in a console and useless in code: telling a 400 (the
 * server refused this patch — a bug in what we sent, and permanent) apart from a 500 or
 * a dropped connection (retry, and say "try again") meant matching a substring of a
 * string built for humans. The two need different copy and different recovery, and the
 * contact write is the first path where getting it wrong is visible to a realtor: a 400
 * from the field whitelist would otherwise read as "the network is down".
 *
 * `code` is the server's OWN error code (`CRM_INVALID_REQUEST` / `CRM_NOT_FOUND` /
 * `CRM_SCOPE_FORBIDDEN` / …), lifted from the JSON body when there is one. It is more
 * stable than the status: the CRM controller answers 400 for both a malformed patch and
 * an ambiguous org, and only the code separates them.
 *
 * ── Why it lives HERE ───────────────────────────────────────────────────────────
 *
 * It is not contacts-specific, and today its only typed consumer is
 * `toContactWriteError` two functions below — the mapping that turns a server 404 into
 * the `ContactNotInYourBookError` this file already defines. Keeping the class beside
 * that mapping is what makes the pair readable. Move it somewhere shared the moment a
 * second surface reads `code`; every throw site is `sendWebotRequest`, so that is a
 * rename, not a migration.
 */
export class WebotServerError extends Error {
  /** HTTP status. `0` when the request never got a response (offline, DNS, CORS). */
  readonly status: number;
  /** The server's `error` field, when the body was JSON. */
  readonly code: string | null;
  /** The server's human `message` field, when the body was JSON. */
  readonly serverMessage: string | null;
  /**
   * The refusal's whole JSON body, when it was a JSON object — for what a route says BESIDE
   * `error` and `message`. Today that is the contact endpoints' `field` / `reason` / `limit`,
   * read by `describeContactValidationFailure` below. Null for any other body.
   */
  readonly details: Readonly<Record<string, unknown>> | null;

  constructor(
    message: string,
    status: number,
    code: string | null,
    serverMessage: string | null,
    details: Readonly<Record<string, unknown>> | null = null
  ) {
    super(message);
    this.name = "WebotServerError";
    this.status = status;
    this.code = code;
    this.serverMessage = serverMessage;
    this.details = details;
    // Same es5 prototype-chain repair as above — without it `instanceof` is false.
    Object.setPrototypeOf(this, WebotServerError.prototype);
  }
}

export function isWebotServerError(error: unknown): error is WebotServerError {
  if (error instanceof WebotServerError) return true;
  return (error as { name?: string } | null)?.name === "WebotServerError";
}

/**
 * What a failed response's body says — read in ONE place, for both request helpers in
 * `WebotServerContext` (`sendWebotRequest` and the CSV export, which cannot share the helper).
 *
 * Every JSON error this server emits is `{ error, message, statusCode }`, and some routes say
 * more beside them (the contact endpoints name the refused `field`, see `WebotServerError.details`).
 * A body that is HTML (a proxy's error page), empty, or JSON that is not an object yields nulls:
 * reading it is best-effort and must never mask the original failure with a parse failure.
 */
export function readWebotErrorBody(
  contentType: string,
  text: string
): { code: string | null; serverMessage: string | null; details: Readonly<Record<string, unknown>> | null } {
  const nothing = { code: null, serverMessage: null, details: null };
  if (!contentType.toLowerCase().includes("application/json") || !text) return nothing;
  try {
    const parsed: unknown = JSON.parse(text);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return nothing;
    const body = parsed as Record<string, unknown>;
    return {
      code: typeof body.error === "string" ? body.error : null,
      serverMessage: typeof body.message === "string" ? body.message : null,
      details: body
    };
  } catch {
    return nothing;
  }
}

/**
 * A CREATE refused because that phone number is already in the book.
 *
 * ⚠️ THE SERVER IS THE ONLY RELIABLE DUPLICATE GUARD, and this is why it has to be
 * surfaced rather than treated as a generic failure. The dialog's own `isDuplicatePhone`
 * is computed from `allContacts` — the in-browser array that the CRM page deliberately
 * NEVER loads (25,066 documents for one real user), and that lands asynchronously
 * everywhere else. So on the surface where creating is most prominent, the client-side
 * check is blind and a real collision arrives here or nowhere.
 *
 * The blocking contact may be SOFT-DELETED and still blocks; `serverMessage` says which
 * case it is, in Hebrew, and is what the dialog renders.
 */
export function isContactDuplicateError(error: unknown): error is WebotServerError {
  return isWebotServerError(error) && (error.status === 409 || error.code === "CRM_CONFLICT");
}

/**
 * A write REFUSED because the agency's switch into (or out of) the pooled book is running
 * (the server's plan D4, decision 4): a 503 `CRM_BOOK_BUSY` with `retryable: true` and the
 * plain Hebrew sentence the screen shows. Not queued — the realtor tries again in a few
 * minutes, and the sentence says so. `serverMessage` is preferred over any local copy so
 * the wording is the server's own.
 */
export function isContactBookBusyError(error: unknown): error is WebotServerError {
  return isWebotServerError(error) && error.code === "CRM_BOOK_BUSY";
}

/**
 * What to show for a failed contact write: the server's own sentence for a busy book or a
 * refusal it bothered to explain (a 400 / 403 with a message), the caller's fallback
 * otherwise. One place, so the busy message reads the same on every surface.
 */
export function describeContactWriteFailure(error: unknown, fallback: string): string {
  if (isContactBookBusyError(error) && error.serverMessage) return error.serverMessage;
  if (isWebotServerError(error) && (error.status === 403 || error.code === "CRM_SCOPE_FORBIDDEN")) {
    return "אין לך הרשאה לפעולה הזו. פנו למנהל/ת המשרד.";
  }
  return fallback;
}

/**
 * A request the server REFUSED AS INVALID — a 400 `CRM_INVALID_REQUEST`, from any CRM route.
 * Permanent: the same request sent again is refused again, so "try again" is the one thing
 * not to say about it.
 */
export function isContactValidationError(error: unknown): error is WebotServerError {
  return isWebotServerError(error) && error.code === "CRM_INVALID_REQUEST";
}

/**
 * The label the CONTACT FORM shows for each field the server can refuse — the i18n key its
 * input renders with (`EditContactDialog`, and the tag field's own component), and the Hebrew
 * that key falls back to there. The `newContact.v2.*` keys are in no locale file: the form
 * types their Hebrew inline, so each default below is the SAME literal as the form's, and
 * `contactWriteErrors.test.ts` scans the form's source to hold them equal.
 *
 * The lower end of a range reads under its range's label, and the contact's id is its phone.
 * A field that is not here gets the sentence that names no field.
 */
export const CONTACT_FIELD_LABELS: Readonly<Record<string, { key: string; defaultValue?: string }>> = {
  contactId: { key: "contactsPage.tableColumns.callerPhoneNumber" },
  callerPhoneNumber: { key: "contactsPage.tableColumns.callerPhoneNumber" },
  additionalPhoneNumbers: { key: "contactsPage.tableColumns.additionalPhoneNumbers" },
  callerFirstName: { key: "contactsPage.tableColumns.callerFirstName" },
  callerLastName: { key: "contactsPage.tableColumns.callerLastName" },
  mail: { key: "contactsPage.tableColumns.mail" },
  tehudatZehut: { key: "contactsPage.tableColumns.tehudatZehut" },
  address: { key: "contactsPage.tableColumns.address" },
  tags: { key: "contactsPage.tableColumns.tags" },
  interestedInPropertyTypes: { key: "listingsMatch.filters.propertyTypesTitle" },
  additionalNotes: { key: "contactsPage.newContact.v2.aboutClientTitle", defaultValue: "על הלקוח" },
  contactOrigin: { key: "contactsPage.newContact.v2.leadSourceTitle", defaultValue: "מקור הליד" },
  customerTypes: { key: "contactsPage.newContact.v2.customerType", defaultValue: "סוג לקוח" },
  crmStatus: { key: "contactsPage.newContact.v2.statusTitle", defaultValue: "סטטוס" },
  customerBudget: { key: "contactsPage.newContact.v2.budget", defaultValue: "תקציב" },
  customerBudgetMin: { key: "contactsPage.newContact.v2.budget", defaultValue: "תקציב" },
  customerBudgetRent: { key: "contactsPage.newContact.v2.rentBudget", defaultValue: "תקציב שכירות (לחודש)" },
  customerBudgetRentMin: { key: "contactsPage.newContact.v2.rentBudget", defaultValue: "תקציב שכירות (לחודש)" },
  roomsMin: { key: "contactsPage.newContact.v2.rooms", defaultValue: "חדרים" },
  roomsMax: { key: "contactsPage.newContact.v2.rooms", defaultValue: "חדרים" },
  floorsMin: { key: "contactsPage.newContact.v2.floor", defaultValue: "קומה" },
  floorsMax: { key: "contactsPage.newContact.v2.floor", defaultValue: "קומה" },
  squareMeterMin: { key: "contactsPage.newContact.v2.builtArea", defaultValue: "שטח בנוי (מ״ר)" },
  squareMeterMax: { key: "contactsPage.newContact.v2.builtArea", defaultValue: "שטח בנוי (מ״ר)" },
  plotSizeMin: { key: "contactsPage.newContact.v2.plotSize", defaultValue: "שטח מגרש (מ״ר)" },
  plotSizeMax: { key: "contactsPage.newContact.v2.plotSize", defaultValue: "שטח מגרש (מ״ר)" },
  windDirectionsCount: { key: "contactsPage.newContact.v2.windDirections", defaultValue: "כיווני אוויר" }
};

/** The sentences, in `src/locales/{he,en}.json` under this prefix. */
const REFUSED = "contactsPage.error.refused";

/**
 * What to tell a realtor whose contact the server REFUSED over one field — naming the field by
 * the label the form shows for it. `null` for any other failure, so the caller keeps its own
 * copy for "the network is down" and the rest.
 *
 * ── Why it exists ───────────────────────────────────────────────────────────────────
 *
 * The contact form answered every 400 with "שמירת הלקוח נכשלה. נסו שוב". For a refusal that is
 * the wrong advice twice over: it names nothing to fix, and trying again sends the same form
 * to the same refusal. On 2026-10-12 one realtor pressed save eleven times in two minutes.
 * The server now says which field and why as DATA beside its English sentence
 * (`CrmValidationDetails` in its `crm/crmErrors.ts`: `field`, `reason`, `limit`), and this turns
 * that into a sentence. The reasons handled below are a COPY of the server's
 * `CRM_VALIDATION_REASONS` — there is no build-time link. One the server adds and this does not
 * know reads as "the value in this field is not valid", which is true of every one of them.
 *
 * ── The cases that are not "<field> is wrong" ────────────────────────────────────────────
 *
 *  - AN AREA. Everything the server can refuse inside `interestedInLocations` — no city, a
 *    street name over its cap, a house number that is not one, a years-old entry stored as plain
 *    text — arrives under that one field, and the realtor's move is the same for all of them:
 *    take the area out and choose it again. Saying "the text in "אזורים מבוקשים" is too long"
 *    about a picker nobody types in would be the server's truth and the screen's nonsense.
 *  - THE PHONE. Its own sentence: it is the one field whose refusal is about digits.
 *  - A FIELD THE FORM SHOULD NOT HAVE SENT, or sent in the wrong shape. Nothing the realtor typed,
 *    and nothing they can change: the sentence says it is ours and where to turn.
 *  - NO DETAILS AT ALL (a server that predates them, or a refusal about the request as a whole)
 *    or a field the form has no label for: the sentence says only what is known — it was not
 *    saved, a detail may be wrong — and never "try again".
 *
 * `translate` is the caller's i18n function (`t`): the labels and the sentences are whatever
 * the app's language says.
 */
export function describeContactValidationFailure(
  error: unknown,
  translate: (key: string, options?: Record<string, unknown>) => unknown
): string | null {
  if (!isContactValidationError(error)) return null;

  const details = error.details ?? {};
  const field = typeof details.field === "string" ? details.field : null;
  const reason = typeof details.reason === "string" ? details.reason : null;
  // A cap is only worth saying when it is one: a whole number above zero.
  const cap = typeof details.limit === "number" && Number.isInteger(details.limit) && details.limit > 0 ? details.limit : null;
  const limit = cap === null ? null : cap.toLocaleString("he-IL");

  const say = (sentence: string, values: Record<string, unknown> = {}): string => String(translate(`${REFUSED}.${sentence}`, values));

  if (field === "interestedInLocations") {
    return reason === "too_many" && limit ? say("tooManyAreas", { limit }) : say("badArea");
  }
  if (reason === "invalid_phone") return say("badPhone");
  if (reason === "not_writable" || reason === "wrong_type") return say("systemFault");

  const source = field && Object.prototype.hasOwnProperty.call(CONTACT_FIELD_LABELS, field) ? CONTACT_FIELD_LABELS[field] : null;
  const translated = source ? translate(source.key, source.defaultValue === undefined ? undefined : { defaultValue: source.defaultValue }) : null;
  const label = typeof translated === "string" ? translated.trim() : "";
  if (!label) return say("unknown");

  switch (reason) {
    case "too_long":
      return limit ? say("tooLongWithLimit", { field: label, limit }) : say("tooLong", { field: label });
    case "too_many":
      return limit ? say("tooManyWithLimit", { field: label, limit }) : say("tooMany", { field: label });
    case "not_a_number":
    case "out_of_range":
      return say("badNumber", { field: label });
    case "required":
      return say("missing", { field: label });
    default:
      return say("badValue", { field: label });
  }
}

/**
 * The one translation the contact write path needs: a server 404 IS "this contact is not
 * in your book".
 *
 * Done here rather than inside `sendWebotRequest` on purpose. That helper serves ~100
 * endpoints, and a 404 from a Facebook lead or a location override means something
 * completely different — blanket-mapping it would put "איש הקשר אינו ברשימה שלך" on
 * screens that have no contact on them.
 *
 * `CRM_NOT_FOUND` is checked alongside the status because the code is the stabler of the
 * two: the endpoint returns exactly one 404 today, but the code says WHICH.
 *
 * Anything else is returned untouched, so a 400 from the field whitelist keeps its own
 * identity instead of being laundered into a friendlier but wrong error.
 */
export function toContactWriteError(error: unknown, contactId: string): unknown {
  if (isContactNotInYourBookError(error)) return error;
  if (isWebotServerError(error) && (error.status === 404 || error.code === "CRM_NOT_FOUND")) {
    return new ContactNotInYourBookError(contactId);
  }
  return error;
}
